import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppAuthGuard } from './authentication/keycloak/keycloak-guard';
import { keycloakInitializer } from './authentication/keycloak/keycloak-initializer';
import { PageRestrictionGuard } from './authorization/page-restriction.guard';
import { ClientsService } from './clients/clients.service';
import { EnvService } from './configuration/env.service';
import { EnvServiceProvider } from './configuration/env.service.provider';
import { DockingModule } from './docking/docking.module';
import { CustomErrorHandler } from './error-handling/custom-error-handler';
import { FopModeService } from './fop-mode/fop-mode.service';
import { LayoutsModule } from './layouts/layouts.module';
import { LocationContextReset } from './location-context/initializers/location-context-reset-initializer';
import { LocationContextGuard } from './location-context/location-context.guard';
import { LocationContextModule } from './location-context/location-context.module';
import { LocationContextService } from './location-context/location-context.service';
import { LogService } from './logging/log.service';
import { MaterialModule } from './material/material.module';
import { NavigationModule } from './navigation/navigation.module';
import { NotificationsModule } from './notifications/notifications.module';
import { metaReducers, ROOT_REDUCERS } from './reducers';
import { correlationIdInitializer } from './server-communication/initializers/correlation-id-initializer';
import { NoNetworkDialogBodyComponent } from './server-communication/notifications/no-network/no-network-dialog-body/no-network-dialog-body.component';
import { NoServerDialogBodyComponent } from './server-communication/notifications/no-server/no-server-dialog-body/no-server-dialog-body.component';
import { ConfirmationDialogComponent } from './shared/confirmation-dialog/confirmation-dialog.component';
import { MopOperatorsDialogComponent } from './docking/conmon/mop-operators-dialog/mop-operators-dialog.component';
import { GeneralMessageDialogComponent } from './shared/general-message/general-message-dialog.component';
import { TimeCounterModule } from './time-counter/time-counter.module';
import { RouterModule } from '@angular/router';
import { ConMonDeactivateGuard } from './docking/conmon/conmon.guard';
import { LicenseModule } from './license/license.module';
import { WebSocketService } from './server-communication/services/websocket.service';

@NgModule({
    declarations: [
        AppComponent,
        GeneralMessageDialogComponent,
        ConfirmationDialogComponent,
        MopOperatorsDialogComponent
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        RouterModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        KeycloakAngularModule,
        ReactiveFormsModule,
        MaterialModule,
        LayoutsModule,
        NavigationModule,
        LocationContextModule,
        DockingModule,
        TimeCounterModule,
        NotificationsModule,
        FormsModule,
        LicenseModule,
        StoreModule.forRoot(ROOT_REDUCERS, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictActionSerializability: true,
                strictStateSerializability: true
            }
        }),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
        EffectsModule.forRoot([])
    ],
    providers: [
        EnvServiceProvider,
        AppAuthGuard,
        ConMonDeactivateGuard,
        {
            provide: APP_INITIALIZER,
            useFactory: keycloakInitializer,
            multi: true,
            deps: [KeycloakService, EnvService, FopModeService, LogService, WebSocketService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: correlationIdInitializer,
            multi: true,
            deps: [ClientsService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: LocationContextReset,
            multi: true,
            deps: [LocationContextService]
        },
        { provide: ErrorHandler, useClass: CustomErrorHandler },
        { provide: Window, useValue: window },
        PageRestrictionGuard,
        LocationContextGuard
    ],
    bootstrap: [AppComponent],
    entryComponents: [
        GeneralMessageDialogComponent,
        ConfirmationDialogComponent,
        MopOperatorsDialogComponent,
        NoNetworkDialogBodyComponent,
        NoServerDialogBodyComponent
    ]
})
export class AppModule {}
